import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Check, CheckCheck, FileText, Forward, Loader2, Paperclip, Send, Trash2, UserSquare2 } from "lucide-react";
import { ForwardSheet } from "./ForwardSheet";
import { DeleteSheet, deleteConversation, deleteMessage, useLongPress } from "./DeleteSheet";
import { Link, useNavigate } from "react-router-dom";
import { Attachment } from "./Attachment";
import { toast } from "sonner";
import { api, errText } from "../../connect/api";
import { playTone } from "../../connect/sound";
import { useConnect } from "../../connect/store";
import { Avatar, clock, Header, roleLine, Spinner } from "./ui";

/** admin=true → CEO puppet mode: messages sent via /admin/conversations/{id}/reply and the DM profile is treated as "me". */
export const ChatThread = ({ cid, admin = false, back }) => {
  const { me, refreshBadges } = useConnect();
  const [msgs, setMsgs] = useState([]);
  const [conv, setConv] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [fwd, setFwd] = useState(null);
  const [del, setDel] = useState(null);
  const [delConv, setDelConv] = useState(false);
  const nav = useNavigate();
  const pressProps = useLongPress((m) => setDel(m));
  const [loading, setLoading] = useState(true);
  const [typing, setTyping] = useState(false);
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef(null);
  const lastTyping = useRef(0);
  const bottom = useRef(null);
  const box = useRef(null);
  const first = useRef(true);

  const load = useCallback(async () => {
    let data;
    try { ({ data } = await api.get(`/conversations/${cid}/messages`, { params: { limit: 40 } })); }
    catch (e) { if (e.response?.status === 404) { toast.info("This conversation was deleted"); nav(back || "/connect/chats", { replace: true }); return; } throw e; }
    setConv(data.conversation);
    setTyping(!!data.typing);
    setHasMore(data.has_more);
    setMsgs((prev) => {
      const ids = new Set(data.items.map((m) => m.id));
      const windowStart = data.items[0]?.at;
      const removed = prev.some((m) => !ids.has(m.id) && (!windowStart || m.at >= windowStart));
      if (removed) return [...prev.filter((m) => windowStart && m.at < windowStart), ...data.items];
      if (prev.length && prev[prev.length - 1]?.id === data.items[data.items.length - 1]?.id && prev.length >= data.items.length) {
        const seen = Object.fromEntries(data.items.map((m) => [m.id, m.seen_at]));
        return prev.some((m) => seen[m.id] !== undefined && seen[m.id] !== m.seen_at) ? prev.map((m) => (seen[m.id] !== undefined ? { ...m, seen_at: seen[m.id] } : m)) : prev;
      }
      const lastNew = data.items[data.items.length - 1];
      const selfId = admin && data.conversation?.dummy_id ? data.conversation.dummy_id : me.id;
      if (prev.length && lastNew && lastNew.id !== prev[prev.length - 1]?.id && lastNew.sender_id !== selfId) playTone(admin || data.conversation?.with_ceo ? "ceo" : "message");
      return data.items;
    });
    setLoading(false);
  }, [cid, admin, me.id, nav, back]);

  useEffect(() => {
    first.current = true;
    setLoading(true);
    load().catch((e) => toast.error(errText(e)));
    if (admin) api.post(`/admin/conversations/${cid}/seen`).catch(() => {});
    const t = setInterval(() => load().catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [cid, load, admin]);

  useEffect(() => {
    if (!msgs.length) return;
    bottom.current?.scrollIntoView({ behavior: first.current ? "auto" : "smooth" });
    first.current = false;
    refreshBadges();
  }, [msgs.length, refreshBadges]);

  const older = async () => {
    if (!msgs.length) return;
    const { data } = await api.get(`/conversations/${cid}/messages`, { params: { before: msgs[0].at, limit: 40 } });
    setMsgs((prev) => [...data.items, ...prev]);
    setHasMore(data.has_more);
  };

  const people = conv?.people || {};
  const dummyId = conv?.dummy_id;
  const myId = admin && dummyId ? dummyId : me.id;
  const otherId = conv?.participants?.find((p) => p !== myId);
  const other = people[otherId];
  const dummy = people[dummyId];
  const [otherName, setOtherName] = useState("");
  useEffect(() => {
    if (other?.name) setOtherName(other.name);
    else if (otherId && !other) api.get(`/staff/${otherId}`).then((r) => { setOtherName(r.data.name); setConv((c) => ({ ...c, people: { ...(c?.people || {}), [otherId]: r.data } })); }).catch(() => {});
  }, [otherId, other]);

  const onType = (v) => {
    setText(v);
    if (v && Date.now() - lastTyping.current > 2500) { lastTyping.current = Date.now(); api.post(`/conversations/${cid}/typing`).catch(() => {}); }
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(1);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/conversations/${cid}/attachments`, fd, { onUploadProgress: (ev) => setUploading(Math.max(1, Math.round((ev.loaded / (ev.total || file.size)) * 100))) });
      setMsgs((prev) => [...prev, data]);
    } catch (ex) { toast.error(errText(ex)); } finally { setUploading(0); if (fileRef.current) fileRef.current.value = ""; }
  };

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const { data } = admin ? await api.post(`/admin/conversations/${cid}/reply`, { text: t }) : await api.post(`/conversations/${cid}/messages`, { text: t });
      setMsgs((prev) => [...prev, data]);
      setText("");
    } catch (ex) { toast.error(errText(ex)); } finally { setBusy(false); }
  };

  const sub = typing ? "typing…" : roleLine(other);
  const trashBtn = <button type="button" onClick={() => setDelConv(true)} className="p-2 rounded-full text-slate-400 hover:text-red-300 hover:bg-white/10" title={me.is_ceo ? "Delete conversation" : "Delete chat for me"} aria-label="Delete conversation" data-testid="chat-delete-conversation-button"><Trash2 size={18} /></button>;
  const removeLocal = (mid) => setMsgs((prev) => prev.filter((m) => m.id !== mid));
  return (
    <div className="h-full flex flex-col" data-testid="chat-thread">
      <Header title={otherName || "…"} sub={<span className={typing ? "text-emerald-300 animate-pulse" : ""} data-testid="chat-typing-indicator">{sub}</span>} back={back || "/connect/chats"}
        right={otherId && (me.is_ceo ? <span className="flex items-center gap-1">{trashBtn}<Link to={`/connect/admin/send-as?to=${otherId}`} className="nxc-btn nxc-btn-gold h-8 !px-2.5 text-[11px]" data-testid="chat-send-as-button"><UserSquare2 size={13} /> Send as…</Link><Link to={`/connect/staff/${otherId}`} data-testid="chat-header-profile-link"><Avatar name={otherName} src={other?.avatar} size={36} /></Link></span> : <span className="flex items-center gap-1">{trashBtn}<span data-testid="chat-header-profile-link"><Avatar name={otherName} src={other?.avatar} size={36} /></span></span>)} />
      {admin && dummy && (
        <div className="nxc-puppet-banner px-3 py-2 text-xs font-medium flex items-center gap-2 shrink-0" data-testid="puppet-banner">
          <UserSquare2 size={15} className="shrink-0" />
          <span className="font-mono">Replying as <b>{dummy.name}</b> — {dummy.department} — {dummy.office}</span>
        </div>
      )}
      <div ref={box} className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="chat-messages">
        {loading && <div className="flex justify-center py-10"><Spinner /></div>}
        {hasMore && <div className="flex justify-center"><button onClick={older} className="nxc-btn nxc-btn-ghost text-xs h-8" data-testid="chat-load-older">Load earlier messages</button></div>}
        {msgs.map((m, i) => {
          const mine = m.sender_id === myId;
          const sender = people[m.sender_id] || (mine ? me : other) || {};
          const prevSame = i > 0 && msgs[i - 1].sender_id === m.sender_id;
          const nextSame = i < msgs.length - 1 && msgs[i + 1].sender_id === m.sender_id;
          const shape = mine
            ? `${prevSame ? "rounded-tr-md" : "rounded-tr-2xl"} ${nextSame ? "rounded-br-md" : "rounded-br-2xl"} rounded-l-2xl`
            : `${prevSame ? "rounded-tl-md" : "rounded-tl-2xl"} ${nextSame ? "rounded-bl-md" : "rounded-bl-2xl"} rounded-r-2xl`;
          return (
            <div key={m.id} className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"} ${prevSame ? "mt-0.5" : "mt-3"} nxc-fade-in`} data-testid={`message-${m.id}`}>
              {!mine && (nextSame ? <span className="w-7 shrink-0" /> : (
                me.is_ceo && sender.id ? <Link to={`/connect/staff/${sender.id}`} className="shrink-0" data-testid={`message-avatar-${m.id}`}><Avatar name={sender.name} src={sender.avatar} size={28} ceo={sender.is_ceo} /></Link> : <span className="shrink-0" data-testid={`message-avatar-${m.id}`}><Avatar name={sender.name} src={sender.avatar} size={28} ceo={sender.is_ceo} /></span>
              ))}
              {me.is_ceo && mine && <button onClick={() => setFwd({ m, from: sender.name || me.name })} className="p-1.5 rounded-full text-slate-500 hover:text-amber-300 hover:bg-white/5 shrink-0 mb-5" title="Forward" data-testid={`forward-${m.id}`}><Forward size={14} /></button>}
              <div className={`max-w-[75%] lg:max-w-[60%] xl:max-w-[720px] flex flex-col ${mine ? "items-end" : "items-start"}`}>
                {!mine && !prevSame && <div className={`text-[11px] mb-1 ml-3 ${sender.is_ceo ? "text-amber-300 font-semibold" : "text-slate-400"}`} data-testid={`message-sender-${m.id}`}>{sender.name || otherName}{sender.is_ceo ? " · Group CEO" : ""}</div>}
                <div {...pressProps(m)} className={`select-none px-3.5 py-2 text-[15px] leading-snug whitespace-pre-wrap break-words cursor-pointer active:opacity-80 ${shape} ${m.auto && admin ? "nxc-bubble-auto" : mine ? "bg-[#0a7cff] text-white" : "bg-[#303030] text-slate-100"}`} data-testid={`message-bubble-${m.id}`}>
                  {admin && (m.auto || m.via_ceo) && <div className="text-[10px] uppercase tracking-wider opacity-80 mb-0.5 flex items-center gap-1">{m.auto ? <><Bot size={11} /> Auto-reply</> : "Sent by CEO"}</div>}
                  {m.attachment ? <Attachment att={m.attachment} caption={m.text} mine={mine} /> : m.text}
                </div>
                {!nextSame && (
                  <div className={`text-[10px] mt-1 flex items-center gap-1 text-slate-500 ${mine ? "mr-1" : "ml-3"}`}>{clock(m.at)}{mine && (m.seen_at ? <><CheckCheck size={13} className={m.auto && admin ? "text-amber-300" : "text-sky-400"} title={`Seen ${clock(m.seen_at)}`} data-testid="receipt-seen" />{me.is_ceo && <span className="text-sky-400/90" data-testid={`receipt-seen-by-${m.id}`}>Seen by {otherName || "staff"} · {clock(m.seen_at)}</span>}</> : <Check size={13} className="opacity-70" title="Delivered" data-testid="receipt-delivered" />)}</div>
                )}
              </div>
              {me.is_ceo && !mine && <button onClick={() => setFwd({ m, from: sender.name || otherName })} className="p-1.5 rounded-full text-slate-500 hover:text-amber-300 hover:bg-white/5 shrink-0 mb-5" title="Forward" data-testid={`forward-${m.id}`}><Forward size={14} /></button>}
            </div>
          );
        })}
        {fwd && <ForwardSheet message={fwd.m} fromName={fwd.from} onClose={() => setFwd(null)} />}
        {del && (
          <DeleteSheet title="Message" preview={del.text || del.attachment?.name} isCeo={me.is_ceo} onClose={() => setDel(null)}
            onForward={me.is_ceo ? () => setFwd({ m: del, from: (people[del.sender_id] || {}).name || otherName || me.name }) : null}
            onEveryone={async () => { await deleteMessage(cid, del.id, "everyone"); removeLocal(del.id); toast.success("Deleted for everyone"); }}
            onMe={async () => { await deleteMessage(cid, del.id, "me"); removeLocal(del.id); toast.success("Deleted for you"); }} />
        )}
        {delConv && (
          <DeleteSheet title="Delete conversation" preview={`Chat with ${otherName}`} isCeo={me.is_ceo} onClose={() => setDelConv(false)}
            onEveryone={async () => { await deleteConversation(cid, "everyone"); toast.success("Conversation deleted for everyone"); nav(back || "/connect/chats", { replace: true }); }}
            onMe={async () => { await deleteConversation(cid, "me"); toast.success("Chat cleared for you"); nav(back || "/connect/chats", { replace: true }); }} />
        )}
        <div ref={bottom} />
      </div>
      <form onSubmit={send} className="shrink-0 border-t border-white/10 bg-[#0a111e] px-3 py-2 flex items-end gap-2" style={{ paddingBottom: "max(8px, env(safe-area-inset-bottom))" }}>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="chat-attach-input" />
        {(me.is_ceo || (me.perms || {}).attachments !== false) && <button type="button" onClick={() => fileRef.current?.click()} disabled={!!uploading} className="nxc-btn nxc-btn-ghost w-11 h-11 !px-0 rounded-full shrink-0" data-testid="chat-attach-button" aria-label="Attach photo or PDF">{uploading ? <span className="text-[10px] font-bold text-blue-300">{uploading}%</span> : <Paperclip size={18} />}</button>}
        <textarea rows={1} value={text} onChange={(e) => onType(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
          placeholder={admin && dummy ? `Reply as ${dummy.name}…` : "Type a message"} className="nxc-input resize-none max-h-32 py-2.5" data-testid="chat-message-composer-input" />
        <button className="nxc-btn nxc-btn-primary w-11 h-11 !px-0 rounded-full shrink-0" disabled={busy || !text.trim()} data-testid="chat-send-button" aria-label="Send"><Send size={18} /></button>
      </form>
    </div>
  );
};
